/**
 * Curated handwriting fonts a user can pick for the inside message of their
 * card. `id` is the Thankster `thanksterFontID` passed to the selectFont API.
 *
 * Previews are hosted statically at /card-fonts/<id>.png so the Thankster
 * partner API key is never exposed to the client. They span a range so users
 * can pick whatever reads closest to their own handwriting.
 */
export interface CardFont {
  id:    number;   // thanksterFontID
  name:  string;   // Thankster font name (internal)
  label: string;   // user-facing style label
  /**
   * CSS font stack used ONLY for on-screen previews. Browsers can't load
   * Thankster's actual print fonts, so each style maps to the closest web
   * handwriting font (Caveat for the print styles, scripts for the rest) so a
   * preview reads like the handwritten card we mail, not a typed serif letter.
   */
  previewFont:   string;
  previewWeight: number;
}

export const CARD_FONTS: CardFont[] = [
  { id: 207, name: 'Bold Hand',        label: 'Bold print',     previewFont: '"Caveat", cursive',         previewWeight: 700 },
  { id: 60,  name: 'H.S. Righty',      label: 'Neat print',     previewFont: '"Caveat", cursive',         previewWeight: 500 },
  { id: 380, name: 'Righty Regular',   label: 'Casual print',   previewFont: '"Caveat", cursive',         previewWeight: 400 },
  { id: 191, name: 'Homemaker (Neat)', label: 'Rounded print',  previewFont: '"Caveat", cursive',         previewWeight: 400 },
  { id: 77,  name: 'Miss Betty',       label: 'Soft script',    previewFont: '"Dancing Script", cursive', previewWeight: 600 },
  { id: 248, name: 'Buttercup',        label: 'Elegant script', previewFont: '"Great Vibes", cursive',     previewWeight: 400 },
];

/** Default when a user has not chosen one (Casual print, a friendly neutral). */
export const DEFAULT_CARD_FONT_ID = 380;

export function isValidCardFontId(id: unknown): id is number {
  return typeof id === 'number' && CARD_FONTS.some((f) => f.id === id);
}

/**
 * The preview font stack + weight for a given font id, falling back to the
 * default (Casual print) for unknown/missing ids. Used by the card previews so
 * what the user sees on screen matches the handwriting style they picked.
 */
export function getCardFontPreview(id: number | null | undefined): { family: string; weight: number } {
  const font = CARD_FONTS.find((f) => f.id === id)
    ?? CARD_FONTS.find((f) => f.id === DEFAULT_CARD_FONT_ID)!;
  return { family: font.previewFont, weight: font.previewWeight };
}
